/** @jsx jsx */

import React, {
  Fragment as _,
  useState,
  useContext,
  useRef,
  useEffect,
  useLayoutEffect,
} from 'react';
import { css, jsx } from '@emotion/core';
import { colors } from 'styles/variables';
import getStore from 'services/getStore';
import Modal from 'components/UI/Modal';
import getTopics from 'services/apiHelpers/getTopics';
import { useSelector } from 'react-redux';
import getInteractionsInTopic from 'services/apiHelpers/getInteractionsInTopic';
import EditorBtn from 'components/UI/EditorBtn';
import Tick from 'components/UI/Tick';
import { getBoundingClientObj } from 'react-select/src/utils';

const { lPink, lGrey0, lGrey1, lTeal0 } = colors;
const [darkGrey, lightGrey, blue] = ['#28292b', '#f3f6ff', '#98add9'];
const topicPanelWidth = 190;

export const moduleNavigationActions = {
  NEXT: 'next',
  EXIT: 'exit',
  SET_MODULE_COMPLETE: 'setModuleComplete',
  SET_MODULE_COMPLETE_AND_EXIT: 'setModuleCompleteAndExit',
  RESET_MODULE: 'resetModule',
};

export const moduleNavigationActionsList = Object.values(
  moduleNavigationActions
);

const {
  NEXT,
  EXIT,
  SET_MODULE_COMPLETE,
  SET_MODULE_COMPLETE_AND_EXIT,
  RESET_MODULE,
} = moduleNavigationActions;

const TopicPanel = ({ name, color = darkGrey, children }) => {
  const [titleHeight, setTitleHeight] = useState(30);
  const height = 135;

  const title = useRef<HTMLDivElement>(null);

  useLayoutEffect(() => {
    if (title.current) {
      const { height } = title.current.getBoundingClientRect();
      setTitleHeight(height);
    }
  }, []);

  return (
    <div
      css={css`
        width: ${topicPanelWidth}px;
        height: ${height}px;
        margin: 5px;
        border-radius: 10px;
        overflow: hidden;
        box-shadow: 0 0 0 1px ${color};
      `}
    >
      <div
        ref={title}
        css={css`
          display: flex;
          align-items: center;
          width: 100%;
          min-height: ${titleHeight}px;
          padding: 10px;
          background: ${color};
          font-size: 12px;
          color: #fff;
        `}
      >
        {name}
      </div>
      <div
        css={css`
          height: ${height - titleHeight}px;
          overflow-y: auto;
        `}
      >
        {children}
      </div>
    </div>
  );
};

const InteractionPanel = ({
  interaction,
  usingExternalURL,
  isCurrentLocation,
  isTargetLocation,
  onClick,
}) => {
  const { id } = interaction;
  const { sId, name } = interaction.props;

  return (
    <div
      css={css`
        display: flex;
        justify-content: space-between;
        align-items: center;

        width: 100%;
        min-height: 30px;
        padding: 10px;

        font-size: 12px;

        ${isCurrentLocation &&
          `
        color: ${blue};
        pointer-events: none;
        `}

        ${!usingExternalURL &&
          isTargetLocation &&
          `
          background: ${lightGrey};
          `}

        cursor: pointer;
        transition: all 0.2s;

        &:hover {
          opacity: 0.45;
        }
      `}
      onClick={onClick}
    >
      <div
        css={css`
          padding: 0 10px 0 0;
        `}
      >
        {name}
        {isCurrentLocation ? ' (current)' : ''}
      </div>

      {!usingExternalURL && isTargetLocation && <Tick width={6} height={8} />}
    </div>
  );
};

const ModuleGlobalNavigation = ({
  noActions = false,
  noDefaults = false,
  usingExternalURL = false,
  location = '1/1',
  onUpdate = (value: {}) => {},
  onClose,
}) => {
  const [localLocation, setLocalLocation] = useState<string>(location);

  const topics = getTopics();

  const { currentInteraction } = useSelector((state: any) => state);

  const topicPanelsContainer = useRef<any>(null);

  // set to false on init
  // true only when we legit click to update
  const hasUpdated = useRef<boolean>(false);

  const getLocationAsArray = () => {
    if (
      // [NEXT, EXIT, SET_MODULE_COMPLETE, SET_MODULE_COMPLETE_AND_EXIT].includes(
      moduleNavigationActionsList.includes(localLocation)
    ) {
      return null;
    }

    return localLocation.split('/');
  };

  const updateLocalLocation = location => {
    hasUpdated.current = true;
    setLocalLocation(location);
  };

  const actions = {
    id: null,
    name: 'Actions',
  };

  useLayoutEffect(() => {
    if (!topicPanelsContainer.current) {
      return;
    }

    const topicPanelsContainerHeight = topicPanelsContainer.current.getBoundingClientRect()
      .height;

    if (topicPanelsContainerHeight >= 455) {
      topicPanelsContainer.current.style.height = '455px';
    }
  }, []);

  useEffect(() => {
    if (!hasUpdated.current) {
      return;
    }

    onUpdate({
      location: localLocation,
    });
  }, [localLocation]);

  return (
    <div
      css={css({
        width: '100%',
      })}
    >
      <div
        css={css({
          position: 'relative',
          width: '100%',
          maxHeight: 170 * 2,
        })}
      >
        <div
          ref={topicPanelsContainer}
          css={css`
            left: 0;
            top: 0;
            width: 100%;
            max-height: inherit;
            display: flex;
            flex-wrap: wrap;
            justify-content: center;
            overflow-y: auto;
          `}
        >
          {!noActions && (
            <TopicPanel name="Actions" color={blue}>
              <InteractionPanel
                interaction={{
                  id: 'next',
                  props: {
                    name: 'Next',
                  },
                }}
                usingExternalURL={usingExternalURL}
                isCurrentLocation={null}
                isTargetLocation={localLocation === NEXT}
                onClick={() => {
                  updateLocalLocation(localLocation === NEXT ? location : NEXT);
                }}
              />
              <InteractionPanel
                interaction={{
                  id: 'exit',
                  props: {
                    name: 'Exit',
                  },
                }}
                usingExternalURL={usingExternalURL}
                isCurrentLocation={null}
                isTargetLocation={localLocation === EXIT}
                onClick={() => {
                  updateLocalLocation(localLocation === EXIT ? location : EXIT);
                }}
              />

              <InteractionPanel
                interaction={{
                  id: 'set-module-complete',
                  props: {
                    name: 'Set module complete',
                  },
                }}
                usingExternalURL={usingExternalURL}
                isCurrentLocation={null}
                isTargetLocation={localLocation === SET_MODULE_COMPLETE}
                onClick={() => {
                  updateLocalLocation(
                    localLocation === SET_MODULE_COMPLETE
                      ? location
                      : SET_MODULE_COMPLETE
                  );
                }}
              />

              <InteractionPanel
                interaction={{
                  id: 'set-module-complete-and-exit',
                  props: {
                    name: 'Set module complete\nand exit',
                  },
                }}
                usingExternalURL={usingExternalURL}
                isCurrentLocation={null}
                isTargetLocation={
                  localLocation === SET_MODULE_COMPLETE_AND_EXIT
                }
                onClick={() => {
                  updateLocalLocation(
                    localLocation === SET_MODULE_COMPLETE_AND_EXIT
                      ? location
                      : SET_MODULE_COMPLETE_AND_EXIT
                  );
                }}
              />

              <InteractionPanel
                interaction={{
                  id: 'reset-module',
                  props: {
                    name: 'Reset module',
                  },
                }}
                usingExternalURL={usingExternalURL}
                isCurrentLocation={null}
                isTargetLocation={localLocation === RESET_MODULE}
                onClick={() => {
                  updateLocalLocation(
                    localLocation === RESET_MODULE ? location : RESET_MODULE
                  );
                }}
              />
            </TopicPanel>
          )}
          {topics
            .filter((t: any, topicIndex) => {
              return getInteractionsInTopic(t)?.length;
            })
            .map((t: any, topicIndex) => {
              const topicSId = t.props.sId || t.id;

              const interactions = getInteractionsInTopic(t);

              return (
                <TopicPanel key={topicSId} name={t.props.name}>
                  {interactions.map((i: any, interactionIndex) => {
                    const interactionSId = i.props.sId || i.id;

                    let interactionLocation: any = getLocationAsArray();
                    if (interactionLocation) {
                      interactionLocation = interactionLocation[1];
                    }

                    return (
                      <InteractionPanel
                        key={interactionSId}
                        interaction={i}
                        usingExternalURL={usingExternalURL}
                        isCurrentLocation={i.id === currentInteraction}
                        isTargetLocation={interactionSId == interactionLocation}
                        onClick={() => {
                          updateLocalLocation(
                            interactionSId == interactionLocation
                              ? location
                              : `${topicSId}/${interactionSId}`
                          );
                        }}
                      />
                    );
                  })}
                </TopicPanel>
              );
            })}
        </div>
      </div>
    </div>
  );
};

export default ModuleGlobalNavigation;
